import React, { createContext, useContext, useState, ReactNode, useMemo, useCallback, useRef } from "react";
import { AppService } from "../services/appService";
import {
  AircraftListingDto,
  AircraftListingDetailsDto,
  AircraftRentalDto,
  AircraftRentalDetailsDto,
  PartDto,
  PartDetailsDto,
  BookingDto,
  CartDto,
  CartItemDto,
  CardDetailsDto,
  BillingAddressDto,
  ExperienceDto,
  ExperienceDetailsDto,
  RentalBookingDto,
  UserDto
} from "../types";
import { message } from "antd";
import { useAuth } from "./AuthContext";
import { useCart } from "./CartContext";
import {
  AIRCRAFT_TYPES,
  AIRCRAFT_STATES,
  AIRCRAFT_REGISTRATION_COUNTRIES,
  AIRCRAFT_ORIGIN_COUNTRIES,
  PROPELLER_TYPES,
  ENGINE_STATES,
  ENGINE_COOLINGS
} from "../enums";

interface AppContextType {
  aircraftListings: AircraftListingDto[];
  setAircraftListings: React.Dispatch<React.SetStateAction<AircraftListingDto[]>>;
  aircraftParts: PartDto[];
  setAircraftParts: React.Dispatch<React.SetStateAction<PartDto[]>>;
  experiences: ExperienceDto[];
  setExperiences: React.Dispatch<React.SetStateAction<ExperienceDto[]>>;
  aircraftRentals: AircraftRentalDto[];
  setAircraftRentals: React.Dispatch<React.SetStateAction<AircraftRentalDto[]>>;
  myAircraftListings: any[] | null;
  myAircraftRentals: AircraftRentalDto[] | null;
  myAircraftParts: PartDto[] | null;
  myExperiences: ExperienceDto[] | null;
  setMyAircraftListings: React.Dispatch<React.SetStateAction<any[] | null>>;
  setMyAircraftRentals: React.Dispatch<React.SetStateAction<AircraftRentalDto[] | null>>;
  setMyAircraftParts: React.Dispatch<React.SetStateAction<PartDto[] | null>>;
  setMyExperiences: React.Dispatch<React.SetStateAction<ExperienceDto[] | null>>;
  myListings: any;
  loading: boolean;
  isAircraftListingsLoading: boolean;
  isAircraftPartsLoading: boolean;
  isExperiencesLoading: boolean;
  isAircraftRentalsLoading: boolean;
  isMyAircraftListingsLoading: boolean;
  isMyAircraftRentalsLoading: boolean;
  isMyAircraftPartsLoading: boolean;
  isMyExperiencesLoading: boolean;
  fetchAircraftListings: () => Promise<void>;
  fetchAircraftParts: () => Promise<void>;
  fetchExperiences: () => Promise<void>;
  fetchAircraftRentals: () => Promise<void>;
  fetchMyAircraftListings: () => Promise<any[]>;
  fetchMyAircraftRentals: () => Promise<AircraftRentalDto[]>;
  fetchMyAircraftParts: () => Promise<PartDto[]>;
  fetchMyExperiences: () => Promise<ExperienceDto[]>;
  getAircraftListingDetails: (aircraftListingId: number) => Promise<AircraftListingDetailsDto | null>;
  getAircraftRentalDetails: (aircraftRentalId: number) => Promise<AircraftRentalDetailsDto | null>;
  getAircraftPartDetails: (partId: number) => Promise<PartDetailsDto | null>;
  getExperienceDetails: (experienceId: number) => Promise<ExperienceDetailsDto | null>;
  saveAircraftListing: (listing: AircraftListingDto, details: AircraftListingDetailsDto) => Promise<boolean>;
  saveAircraftRental: (rental: AircraftRentalDto, details: AircraftRentalDetailsDto) => Promise<boolean>;
  saveAircraftPart: (part: PartDto, details: PartDetailsDto) => Promise<boolean>;
  saveExperience: (experience: ExperienceDto, details: ExperienceDetailsDto) => Promise<boolean>;
  addBookings: (bookingDtos: BookingDto[]) => Promise<boolean>;
  requestAircraftRentalBooking: (aircraftRentalBookingDto: RentalBookingDto) => Promise<boolean>;
  getCart: (userDto: UserDto) => Promise<CartDto>;
  getMyAircraftListings: () => Promise<any[]>;
  getMyAircraftRentals: () => Promise<AircraftRentalDto[]>;
  getMyParts: () => Promise<PartDto[]>;
  getMyExperiences: () => Promise<ExperienceDto[]>;
  updateAircraftListing: (listing: AircraftListingDto, details: AircraftListingDetailsDto) => Promise<boolean>;
  updateAircraftRental: (rental: AircraftRentalDto, details: AircraftRentalDetailsDto, removedBookingIds: number[]) => Promise<boolean>;
  updateAircraftPart: (part: PartDto, details: PartDetailsDto) => Promise<boolean>;
  updateExperience: (experience: ExperienceDto, details: ExperienceDetailsDto, removedBookingIds: number[]) => Promise<boolean>;
  toggleAircraftActiveState: (listingId: number) => Promise<boolean>;
  toggleRentalActiveState: (rentalId: number) => Promise<boolean>;
  togglePartActiveState: (partId: number) => Promise<boolean>;
  toggleExperienceActiveState: (experienceId: number) => Promise<boolean>;
  requestViewing: (aircraftListingDto: AircraftListingDto) => Promise<boolean>;
  requestExperienceBooking: (experienceBookingDto: any) => Promise<boolean>;
  processCardPayment: (cartItems: CartItemDto[], cardDetails: CardDetailsDto, billingDetails: BillingAddressDto) => Promise<boolean>;
  configureSellerAccount: (user: UserDto) => Promise<void>;
  updateUserAccount: (user: UserDto) => Promise<void>;
  termsConditions: () => Promise<any>;
  queryLightRag: (question: string, fields?: string[]) => Promise<Record<string, any>[] | null>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const {
    currentUser,
    setCurrentUser,
    isAuthLoading,
    syncSystemCommissionRates,
    getMyOrders,
    isAppDataLoading
  } = useAuth();

  const { fetchCart, addToCartLocally } = useCart();

  const [aircraftListings, setAircraftListings] = useState<AircraftListingDto[]>([]);
  const [isAircraftListingsLoading, setIsAircraftListingsLoading] = useState(false);
  const fetchListingsPromiseRef = useRef<Promise<void> | null>(null);

  const [myAircraftListings, setMyAircraftListings] = useState<any[] | null>(null);
  const [isMyAircraftListingsLoading, setIsMyAircraftListingsLoading] = useState(false);
  const myListingsPromiseRef = useRef<Promise<any[]> | null>(null);

  const [aircraftParts, setAircraftParts] = useState<PartDto[]>([]);
  const [isAircraftPartsLoading, setIsAircraftPartsLoading] = useState(false);
  const fetchPartsPromiseRef = useRef<Promise<void> | null>(null);

  const [myAircraftParts, setMyAircraftParts] = useState<PartDto[] | null>(null);
  const [isMyAircraftPartsLoading, setIsMyAircraftPartsLoading] = useState(false);
  const myPartsPromiseRef = useRef<Promise<PartDto[]> | null>(null);

  const [experiences, setExperiences] = useState<ExperienceDto[]>([]);
  const [isExperiencesLoading, setIsExperiencesLoading] = useState(false);
  const fetchExperiencesPromiseRef = useRef<Promise<void> | null>(null);

  const [myExperiences, setMyExperiences] = useState<ExperienceDto[] | null>(null);
  const [isMyExperiencesLoading, setIsMyExperiencesLoading] = useState(false);
  const myExperiencesPromiseRef = useRef<Promise<ExperienceDto[]> | null>(null);

  const [aircraftRentals, setAircraftRentals] = useState<AircraftRentalDto[]>([]);
  const [isAircraftRentalsLoading, setIsAircraftRentalsLoading] = useState(false);
  const fetchRentalsPromiseRef = useRef<Promise<void> | null>(null);

  const [myAircraftRentals, setMyAircraftRentals] = useState<AircraftRentalDto[] | null>(null);
  const [isMyAircraftRentalsLoading, setIsMyAircraftRentalsLoading] = useState(false);
  const myRentalsPromiseRef = useRef<Promise<AircraftRentalDto[]> | null>(null);

  const fetchAircraftListings = useCallback(async () => {
    if (aircraftListings.length > 0) return;
    if (fetchListingsPromiseRef.current) return fetchListingsPromiseRef.current;

    const performFetch = async () => {
      try {
        setIsAircraftListingsLoading(true);
        const listings = await AppService.getAllAircraftListings();
        const mappedListings = (listings || []).map((item) => {
          const aircraftTypeMatch = AIRCRAFT_TYPES.flatMap((category) => category.options).find((opt) => opt.code === item.type);
          const stateMatch = AIRCRAFT_STATES.find((s) => s.code === item.state);
          const regCountryMatch = AIRCRAFT_REGISTRATION_COUNTRIES.find((c) => c.code === item.registrationCountry);
          const originCountryMatch = AIRCRAFT_ORIGIN_COUNTRIES.find((c) => c.code === item.originCountry);
          const propellerTypeMatch = item.propeller ? PROPELLER_TYPES.find((p) => p.code === item.propeller.type) : null;

          const mappedEngines = (item.engines || []).map(eng => {
            const engineStateMatch = ENGINE_STATES.find(s => s.code === eng.state);
            const engineCoolingMatch = ENGINE_COOLINGS.find(c => c.code === eng.cooling);
            return {
              ...eng,
              stateLabel: engineStateMatch ? engineStateMatch.label : eng.state,
              coolingLabel: engineCoolingMatch ? engineCoolingMatch.label : eng.cooling
            };
          });
          return {
            ...item,
            typeLabel: aircraftTypeMatch ? aircraftTypeMatch.label : item.type,
            stateLabel: stateMatch ? stateMatch.label : item.state,
            regCountryLabel: regCountryMatch ? regCountryMatch.label : item.registrationCountry,
            originCountryLabel: originCountryMatch ? originCountryMatch.label : item.originCountry,
            propellerLabel: propellerTypeMatch ? propellerTypeMatch.label : (item.propeller?.type || "N/A"),
            engines: mappedEngines
          };
        });
        setAircraftListings(mappedListings || []);
      } catch (err) {
        message.error("Failed to load aircraft listings");
      } finally {
        setIsAircraftListingsLoading(false);
        fetchListingsPromiseRef.current = null;
      }
    };

    fetchListingsPromiseRef.current = performFetch();
    return fetchListingsPromiseRef.current;
  }, [aircraftListings.length]);

  const fetchMyAircraftListings = useCallback(async (): Promise<any[]> => {
    if (myAircraftListings !== null) return myAircraftListings;
    if (myListingsPromiseRef.current) return myListingsPromiseRef.current;

    const performFetch = async () => {
      try {
        setIsMyAircraftListingsLoading(true);
        const result = await AppService.getMyListings();
        const data = Array.isArray(result) ? result : [];
        setMyAircraftListings(data);
        return data;
      } catch (err) {
        console.error("Error fetching listings:", err);
        return [];
      } finally {
        setIsMyAircraftListingsLoading(false);
        myListingsPromiseRef.current = null;
      }
    };

    myListingsPromiseRef.current = performFetch();
    return myListingsPromiseRef.current;
  }, [myAircraftListings]);

  const fetchAircraftParts = useCallback(async () => {
    if (aircraftParts.length > 0) return;
    if (fetchPartsPromiseRef.current) return fetchPartsPromiseRef.current;

    const performFetch = async () => {
      try {
        setIsAircraftPartsLoading(true);
        const fetchedParts = await AppService.getAllAircraftParts();
        setAircraftParts(fetchedParts || []);
      } catch (err) {
        console.error("Error loading parts:", err);
        message.error("Failed to load aircraft parts");
      } finally {
        setIsAircraftPartsLoading(false);
        fetchPartsPromiseRef.current = null;
      }
    };

    fetchPartsPromiseRef.current = performFetch();
    return fetchPartsPromiseRef.current;
  }, [aircraftParts.length]);

  const fetchMyAircraftParts = useCallback(async (): Promise<PartDto[]> => {
    if (myAircraftParts !== null) return myAircraftParts;
    if (myPartsPromiseRef.current) return myPartsPromiseRef.current;

    const performFetch = async () => {
      try {
        setIsMyAircraftPartsLoading(true);
        const result = await AppService.getMyParts();
        const data = Array.isArray(result) ? result : [];
        setMyAircraftParts(data);
        return data;
      } catch (err) {
        console.error("Error fetching parts:", err);
        return [];
      } finally {
        setIsMyAircraftPartsLoading(false);
        myPartsPromiseRef.current = null;
      }
    };

    myPartsPromiseRef.current = performFetch();
    return myPartsPromiseRef.current;
  }, [myAircraftParts]);

  const fetchExperiences = useCallback(async () => {
    if (experiences.length > 0) return;
    if (fetchExperiencesPromiseRef.current) return fetchExperiencesPromiseRef.current;

    const performFetch = async () => {
      try {
        setIsExperiencesLoading(true);
        const fetchedExperiences = await AppService.getAllExperiences();
        setExperiences(fetchedExperiences || []);
      } catch (err) {
        console.error("Error loading experiences:", err);
        message.error("Failed to load aviation experiences");
      } finally {
        setIsExperiencesLoading(false);
        fetchExperiencesPromiseRef.current = null;
      }
    };

    fetchExperiencesPromiseRef.current = performFetch();
    return fetchExperiencesPromiseRef.current;
  }, [experiences.length]);

  const fetchMyExperiences = useCallback(async (): Promise<ExperienceDto[]> => {
    if (myExperiences !== null) return myExperiences;
    if (myExperiencesPromiseRef.current) return myExperiencesPromiseRef.current;

    const performFetch = async () => {
      try {
        setIsMyExperiencesLoading(true);
        const result = await AppService.getMyExperiences();
        const data = Array.isArray(result) ? result : [];
        setMyExperiences(data);
        return data;
      } catch (err) {
        console.error("Error fetching experiences:", err);
        return [];
      } finally {
        setIsMyExperiencesLoading(false);
        myExperiencesPromiseRef.current = null;
      }
    };

    myExperiencesPromiseRef.current = performFetch();
    return myExperiencesPromiseRef.current;
  }, [myExperiences]);

  const fetchAircraftRentals = useCallback(async () => {
    if (aircraftRentals.length > 0) return;
    if (fetchRentalsPromiseRef.current) return fetchRentalsPromiseRef.current;

    const performFetch = async () => {
      try {
        setIsAircraftRentalsLoading(true);
        const rentals = await AppService.getAllAircraftRentals();
        setAircraftRentals(rentals || []);
      } catch (err) {
        console.error(err);
        message.error("Failed to load rental fleet data");
      } finally {
        setIsAircraftRentalsLoading(false);
        fetchRentalsPromiseRef.current = null;
      }
    };

    fetchRentalsPromiseRef.current = performFetch();
    return fetchRentalsPromiseRef.current;
  }, [aircraftRentals.length]);

  const fetchMyAircraftRentals = useCallback(async (): Promise<AircraftRentalDto[]> => {
    if (myAircraftRentals !== null) return myAircraftRentals;
    if (myRentalsPromiseRef.current) return myRentalsPromiseRef.current;

    const performFetch = async () => {
      try {
        setIsMyAircraftRentalsLoading(true);
        const result = await AppService.getMyRentals();
        const data = Array.isArray(result) ? result : [];
        setMyAircraftRentals(data);
        return data;
      } catch (err) {
        console.error("Error fetching rentals:", err);
        return [];
      } finally {
        setIsMyAircraftRentalsLoading(false);
        myRentalsPromiseRef.current = null;
      }
    };

    myRentalsPromiseRef.current = performFetch();
    return myRentalsPromiseRef.current;
  }, [myAircraftRentals]);

  const getAircraftListingDetails = async (aircraftListingId: number): Promise<AircraftListingDetailsDto | null> => {
    return await AppService.getAircraftListingDetails(aircraftListingId);
  };

  const getAircraftRentalDetails = async (aircraftRentalId: number): Promise<AircraftRentalDetailsDto | null> => {
    return await AppService.getAircraftRentalDetails(aircraftRentalId);
  };

  const getAircraftPartDetails = async (partId: number): Promise<PartDetailsDto | null> => {
    return await AppService.getAircraftPartDetails(partId);
  };

  const getExperienceDetails = async (experienceId: number): Promise<ExperienceDetailsDto | null> => {
    return await AppService.getExperienceDetails(experienceId);
  };

  const configureSellerAccount = async (user: UserDto): Promise<void> => {
    const success = await AppService.configureSellerAccount(user);
    if (success) {
      setCurrentUser((prevUser) => {
        if (!prevUser) return null;
        return {
          ...prevUser,
          iban: user.iban,
          vatId: user.vatId,
          companyName: user.companyName,
          chargingModel: user.chargingModel,
          planName: user.planName,
        };
      });
      if (syncSystemCommissionRates) {
        try {
          await syncSystemCommissionRates();
          console.log("Comisioanele sistemului au fost sincronizate cu succes.");
        } catch (err) {
          console.error("Eroare la sincronizarea comisioanelor post-configurare:", err);
        }
      }
    }
  };

  const updateUserAccount = async (user: UserDto): Promise<void> => {
    const success = await AppService.updateUserAccount(user);
    if (success) {
      setCurrentUser((prevUser) => {
        if (!prevUser) return null;
        return {
          ...prevUser,
          iban: user.iban,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          phone: user.phone,
          address: user.address,
        };
      });
    }
  };

  const addBookings = async (bookingDtos: BookingDto[]): Promise<boolean> => {
    return await AppService.addBookings(bookingDtos);
  };

 const requestAircraftRentalBooking = async (
     aircraftRentalBookingDto: RentalBookingDto
   ): Promise<boolean> => {
     const success = await AppService.requestAircraftRentalBooking(aircraftRentalBookingDto);

     if (success && aircraftRentalBookingDto.aircraftRental) {
       const aircraftRental = aircraftRentalBookingDto.aircraftRental;
       const price = aircraftRentalBookingDto.estimatedPrice || 0;
       const timeSlots = aircraftRentalBookingDto.timeSlotDtoList || [];

       const details: Record<string, string> = {
         "Aircraft": `${aircraftRental.manufacturer} ${aircraftRental.model}`,
         "Identification": aircraftRental.registration,
         "Checkout": String((aircraftRental as any).checkOutRequired),
         "Location": aircraftRental.baseAirport || "N/A",
         "Payment": "DISABLED"
       };

       if (timeSlots.length > 0) {
         const firstSlot = timeSlots[0];
         if (firstSlot.from) {
           details["Date"] = `${firstSlot.from.day}-${firstSlot.from.month}-${firstSlot.from.year}`;
         }
         timeSlots.forEach((slot, index) => {
           if (slot.from && slot.to) {
             const start = `${String(slot.from.hour).padStart(2, '0')}:${String(slot.from.minute).padStart(2, '0')}`;
             const end = `${String(slot.to.hour).padStart(2, '0')}:${String(slot.to.minute).padStart(2, '0')}`;
             details[`Slot${index + 1}`] = `${start} - ${end}`;
           }
         });
       }

       let thumbnail = "";
       if (aircraftRental.mediaUrls && aircraftRental.mediaUrls.length > 0) {
         thumbnail = aircraftRental.mediaUrls[0];
       } else if (aircraftRental.media && aircraftRental.media.length > 0) {
         const rawData = (aircraftRental.media[0] as any).mediaData;
         if (rawData) {
           if (rawData.startsWith('data:')) {
             thumbnail = rawData;
           } else {
             const prefix = rawData.startsWith('iVBOR') ? 'data:image/png;base64,' : 'data:image/jpeg;base64,';
             thumbnail = prefix + rawData;
           }
         }
       }

       const newCartItem: CartItemDto = {
         type: "AIRCRAFT_RENTAL",
         productId: aircraftRental.rentalId,
         jsonDetails: JSON.stringify(details),
         vatPercentage: 0.019,
         grossValue: price,
         mediaFiles: thumbnail ? [thumbnail] : []
       };

       addToCartLocally(newCartItem);
     }

     return success;
   };

  const requestExperienceBooking = async (
      experienceBookingDto: any
    ): Promise<boolean> => {
      const targetExperienceId = experienceBookingDto?.experience?.experienceId;
      const targetExperience = experiences.find(e => e.experienceId === targetExperienceId) || experienceBookingDto?.experience;
      const price = experienceBookingDto.estimatedPrice || targetExperience?.grossValue || 0;

      const success = await AppService.requestExperienceBooking(experienceBookingDto);

      if (success && targetExperience) {
        const timeSlot = experienceBookingDto.timeSlotDto;

        const startStr = timeSlot?.from ? `${String(timeSlot.from.hour).padStart(2, '0')}:${String(timeSlot.from.minute).padStart(2, '0')}` : "";
        const endStr = timeSlot?.to ? `${String(timeSlot.to.hour).padStart(2, '0')}:${String(timeSlot.to.minute).padStart(2, '0')}` : "";

        const details: Record<string, string> = {
          "Pachet": targetExperience.title || "N/A",
          "Locație": targetExperience.location || "N/A",
          "Data": timeSlot?.from ? `${timeSlot.from.day}-${timeSlot.from.month}-${timeSlot.from.year}` : "",
          "Interval": `${startStr} - ${endStr}`,
          "Payment": "DISABLED"
        };

        let thumbnail = "";
        if (targetExperience.mediaUrls && targetExperience.mediaUrls.length > 0) {
          thumbnail = targetExperience.mediaUrls[0];
        } else if (targetExperience.media && targetExperience.media.length > 0) {
          const rawData = (targetExperience.media[0] as any).mediaData;
          if (rawData) {
            thumbnail = rawData.startsWith('data:') ? rawData : `data:image/jpeg;base64,${rawData}`;
          }
        }

        const newCartItem: any = {
          type: "EXPERIENCE",
          productId: targetExperience.experienceId,
          jsonDetails: JSON.stringify(details),
          vatPercentage: 0.019,
          grossValue: price,
          mediaFiles: thumbnail ? [thumbnail] : []
        };

        addToCartLocally(newCartItem);
      }

      return success;
    };

  const requestViewing = async (aircraftListingDto: AircraftListingDto): Promise<boolean> => {
    return AppService.requestViewing(aircraftListingDto);
  };

  const saveAircraftListing = async (
    listing: AircraftListingDto,
    details: AircraftListingDetailsDto
  ): Promise<boolean> => {
    const savedAircraftListing = await AppService.saveAircraftListing(listing, details);
    if (savedAircraftListing) {
      setAircraftListings((prev) => (prev.length > 0 ? [savedAircraftListing, ...prev] : prev));
      setMyAircraftListings((prev) => (prev ? [savedAircraftListing, ...prev] : [savedAircraftListing]));
      return true;
    }
    return false;
  };

  const saveAircraftRental = async (rental: AircraftRentalDto, details: AircraftRentalDetailsDto) => {
    const saved = await AppService.saveAircraftRental(rental, details);
    if (saved) {
      setAircraftRentals((prev) => (prev.length > 0 ? [saved, ...prev] : prev));
      setMyAircraftRentals((prev) => (prev ? [saved, ...prev] : [saved]));
      return true;
    }
    return false;
  };

  const saveAircraftPart = async (part: PartDto, details: PartDetailsDto) => {
    const saved = await AppService.saveAircraftPart(part, details);
    if (saved) {
      setAircraftParts((prev) => (prev.length > 0 ? [saved, ...prev] : prev));
      setMyAircraftParts((prev) => (prev ? [saved, ...prev] : [saved]));
      return true;
    }
    return false;
  };

  const saveExperience = async (experience: ExperienceDto, details: ExperienceDetailsDto): Promise<boolean> => {
    const saved = await AppService.saveExperience(experience, details);
    if (saved) {
      setExperiences((prev) => (prev.length > 0 ? [saved, ...prev] : prev));
      setMyExperiences((prev) => (prev ? [saved, ...prev] : [saved]));
      return true;
    }
    return false;
  };

  const updateAircraftListing = async (
    listing: AircraftListingDto,
    details: AircraftListingDetailsDto
  ): Promise<boolean> => {
    const updatedListingFromServer = await AppService.updateAircraftListing(listing, details);

    if (updatedListingFromServer) {
      setMyAircraftListings((prev) =>
        prev
          ? prev.map((existing) =>
              existing.listingId === listing.listingId ? updatedListingFromServer : existing
            )
          : [updatedListingFromServer]
      );
      setAircraftListings((prev) =>
        prev ? prev.filter((existing) => existing.listingId !== listing.listingId) : []
      );

      return true;
    }
    return false;
  };

  const updateAircraftRental = async (rental: AircraftRentalDto, details: AircraftRentalDetailsDto): Promise<boolean> => {
    const updated = await AppService.updateAircraftRental(rental, details);
    if (updated) {
      setMyAircraftRentals((prev) =>
        prev ? prev.map((existing) => (existing.rentalId === rental.rentalId ? updated : existing)) : [updated]
      );
      setAircraftRentals((prev) =>
        prev ? prev.filter((existing) => existing.rentalId !== rental.rentalId) : []
      );
      return true;
    }
    return false;
  };

  const updateAircraftPart = async (part: PartDto, details: PartDetailsDto): Promise<boolean> => {
    const updated = await AppService.updateAircraftPart(part, details);
    if (updated) {
      setMyAircraftParts((prev) =>
        prev ? prev.map((existing) => (existing.partId === part.partId ? updated : existing)) : [updated]
      );
      setAircraftParts((prev) =>
        prev ? prev.filter((existing) => existing.partId !== part.partId) : []
      );
      return true;
    }
    return false;
  };

  const updateExperience = async (experience: ExperienceDto, details: ExperienceDetailsDto, removedBookingIds: number[]): Promise<boolean> => {
    const updated = await AppService.updateExperience(experience, details, removedBookingIds);
    if (updated) {
      setMyExperiences((prev) =>
        prev ? prev.map((existing) => (existing.experienceId === experience.experienceId ? updated : existing)) : [updated]
      );
      setExperiences((prev) =>
        prev ? prev.filter((existing) => existing.experienceId !== experience.experienceId) : []
      );
      return true;
    }
    return false;
  };

  const toggleAircraftActiveState = async (listingId: number): Promise<boolean> => {
      try {
        const success = await AppService.toggleAircraftActiveState(listingId);
        if (success) {
          const flip = (list: any[]) => list.map(item => item.listingId === listingId ? { ...item, active: !item.active } : item);
          setMyAircraftListings((prev) => prev ? flip(prev) : null);
          setAircraftListings((prev) => prev ? prev.filter((item) => item.listingId !== listingId) : []);
          message.success("Statusul listării a fost actualizat.");
          return true;
        }
        return false;
      } catch (err) {
        console.error(err);
        return false;
      }
    };

    const toggleRentalActiveState = async (rentalId: number): Promise<boolean> => {
      try {
        const success = await AppService.toggleRentalActiveState(rentalId);
        if (success) {
          const flip = (list: any[]) => list.map(item => item.rentalId === rentalId ? { ...item, active: !item.active } : item);
          setMyAircraftRentals((prev) => prev ? flip(prev) : null);
          setAircraftRentals((prev) => prev ? prev.filter((item) => item.rentalId !== rentalId) : []);
          message.success("Statusul închirierii a fost actualizat.");
          return true;
        }
        return false;
      } catch (err) {
        console.error(err);
        return false;
      }
    };

    const togglePartActiveState = async (partId: number): Promise<boolean> => {
      try {
        const success = await AppService.togglePartActiveState(partId);
        if (success) {
          const flip = (list: any[]) => list.map(item => item.partId === partId ? { ...item, active: !item.active } : item);
          setMyAircraftParts((prev) => prev ? flip(prev) : null);
          setAircraftParts((prev) => prev ? prev.filter((item) => item.partId !== partId) : []);
          message.success("Statusul piesei a fost actualizat.");
          return true;
        }
        return false;
      } catch (err) {
        console.error(err);
        return false;
      }
    };

    const toggleExperienceActiveState = async (experienceId: number): Promise<boolean> => {
      try {
        const success = await AppService.toggleExperienceActiveState(experienceId);
        if (success) {
          const flip = (list: any[]) => list.map(item => item.experienceId === experienceId ? { ...item, active: !item.active } : item);
          setMyExperiences((prev) => prev ? flip(prev) : null);
          setExperiences((prev) => prev ? prev.filter((item) => item.experienceId !== experienceId) : []);
          message.success("Statusul experienței a fost actualizat.");
          return true;
        }
        return false;
      } catch (err) {
        console.error(err);
        return false;
      }
    };

  const processCardPayment = async (
    cartItems: CartItemDto[],
    cardDetails: CardDetailsDto,
    billingDetails: BillingAddressDto
  ): Promise<boolean> => {
    const success = await AppService.processCardPayment(cartItems, cardDetails, billingDetails);

    if (success && typeof getMyOrders === 'function') {
      try {
        await getMyOrders(true);
      } catch (err) {
        console.error("Failed to auto-refresh orders after payment:", err);
      }
    }

    return success;
  };

  const termsConditions = async (): Promise<any> => {
    return await AppService.termsConditions();
  };

  const queryLightRag = useCallback(async (question: string, fields: string[] = ["titlu", "id", "media", "categorie"]): Promise<Record<string, any>[] | null> => {
      try {
        return await AppService.queryLightRag(question, fields);
      } catch (err) {
        console.error("Eroare la interogarea LightRAG din context:", err);
        return null;
      }
    }, []);

  const unifiedLoadingState = useMemo(() => {
    if (isAuthLoading) return true;
    return isAppDataLoading;
  }, [isAuthLoading, isAppDataLoading]);

  const contextValue = useMemo(() => ({
    aircraftListings,
    setAircraftListings,
    aircraftParts,
    setAircraftParts,
    experiences,
    setExperiences,
    aircraftRentals,
    setAircraftRentals,
    myAircraftListings,
    myAircraftRentals,
    myAircraftParts,
    myExperiences,
    setMyAircraftListings,
    setMyAircraftRentals,
    setMyAircraftParts,
    setMyExperiences,
    loading: unifiedLoadingState,
    isAircraftListingsLoading,
    isAircraftPartsLoading,
    isExperiencesLoading,
    isAircraftRentalsLoading,
    isMyAircraftListingsLoading,
    isMyAircraftRentalsLoading,
    isMyAircraftPartsLoading,
    isMyExperiencesLoading,
    fetchAircraftListings,
    fetchAircraftParts,
    fetchExperiences,
    fetchAircraftRentals,
    fetchMyAircraftListings,
    fetchMyAircraftRentals,
    fetchMyAircraftParts,
    fetchMyExperiences,
    getAircraftListingDetails,
    getAircraftRentalDetails,
    getAircraftPartDetails,
    getExperienceDetails,
    saveAircraftListing,
    saveAircraftRental,
    saveAircraftPart,
    saveExperience,
    addBookings,
    requestAircraftRentalBooking,
    getMyAircraftListings: fetchMyAircraftListings,
    getMyAircraftRentals: fetchMyAircraftRentals,
    getMyParts: fetchMyAircraftParts,
    getMyExperiences: fetchMyExperiences,
    updateAircraftListing,
    updateAircraftRental,
    updateAircraftPart,
    updateExperience,
    toggleAircraftActiveState,
    toggleRentalActiveState,
    togglePartActiveState,
    toggleExperienceActiveState,
    requestViewing,
    requestExperienceBooking,
    processCardPayment,
    configureSellerAccount,
    updateUserAccount,
    termsConditions,
    queryLightRag,
    myListings: myAircraftListings
  }), [
    aircraftListings,
    isAircraftListingsLoading,
    aircraftParts,
    isAircraftPartsLoading,
    experiences,
    isExperiencesLoading,
    aircraftRentals,
    isAircraftRentalsLoading,
    myAircraftListings,
    myAircraftRentals,
    myAircraftParts,
    myExperiences,
    isMyAircraftListingsLoading,
    isMyAircraftRentalsLoading,
    isMyAircraftPartsLoading,
    isMyExperiencesLoading,
    unifiedLoadingState,
    fetchAircraftListings,
    fetchAircraftParts,
    fetchExperiences,
    fetchAircraftRentals,
    fetchMyAircraftListings,
    fetchMyAircraftRentals,
    fetchMyAircraftParts,
    fetchMyExperiences,
    saveAircraftListing,
    saveAircraftRental,
    saveAircraftPart,
    saveExperience,
    addBookings,
    requestAircraftRentalBooking,
    requestExperienceBooking,
    updateAircraftListing,
    updateAircraftRental,
    updateAircraftPart,
    updateExperience,
    toggleAircraftActiveState,
    toggleRentalActiveState,
    togglePartActiveState,
    toggleExperienceActiveState,
    requestViewing,
    processCardPayment,
    configureSellerAccount,
    updateUserAccount,
    queryLightRag
  ]);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useAppContext must be used within AppProvider");
  return ctx;
};